import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { trackEvent, trackPageView } from '../services/analytics';
import LoadingPastStoryReels from './LoadingPastStoryReels';

// Use Railway production URL as fallback for native iOS/Android apps
const FALLBACK_API_URL = 'https://taraanaya-prod-production.up.railway.app';
const API_URL = process.env.REACT_APP_BACKEND_URL || FALLBACK_API_URL;

// Fun facts for loading - ORIGINAL
const funFacts = [
  { icon: '🌙', text: 'Your Moon sign reveals your emotional nature and inner self' },
  { icon: '⭐', text: 'There are 27 Nakshatras in Vedic astrology, each with unique qualities' },
  { icon: '🔮', text: 'Saturn returns every 29 years, marking major life transformations' },
  { icon: '✨', text: 'Mercury retrograde occurs 3-4 times per year, affecting communication' },
  { icon: '🌟', text: 'Your rising sign (ascendant) determines how others perceive you' },
  { icon: '💫', text: 'Jupiter takes 12 years to complete one zodiac cycle, bringing growth' },
  { icon: '🪐', text: 'The 7th house governs relationships and marriage in your chart' },
  { icon: '🌠', text: 'Mars rules courage, energy, and your fighting spirit' },
  { icon: '☄️', text: 'Venus influences love, beauty, and artistic talents' },
  { icon: '🌌', text: 'Rahu and Ketu are shadow planets that reveal karmic lessons' }
];

const LoadingStoryGenerator = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentSource = new URLSearchParams(location.search).get('source') || '';
  const isLandingSource = currentSource === 'landing' || currentSource === 'watch';
  const [progress, setProgress] = useState(0);
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [dataGenerated, setDataGenerated] = useState(false);
  const [generationFailed, setGenerationFailed] = useState(false);
  const [birthDataLoaded, setBirthDataLoaded] = useState(null);
  const generationStartedRef = useRef(false);

  // Audio generation handled by LoadingPastStoryReels (embedded)

  // Generate audio DOMAIN BY DOMAIN — each domain loads all 3 languages → 0-100%

  // Load birth data from MongoDB first, then fallback to localStorage
  const loadBirthDataFromMongoDB = async () => {
    try {
      // FIRST: Check if localStorage already has valid birth data (from onboarding)
      // If yes, DON'T overwrite with MongoDB data (prevents data mixing between users)
      const existingLocal = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
      if (existingLocal) {
        try {
          const parsed = JSON.parse(existingLocal);
          if (parsed.name && (parsed.year || parsed.dateOfBirth)) {
            console.log('localStorage already has birth data for:', parsed.name, '- skipping MongoDB load');
            return null; // Let parseBirthData use localStorage
          }
        } catch (e) { /* ignore */ }
      }
      
      const sessionId = localStorage.getItem('birth_session_id');
      if (sessionId) {
        console.log('Loading birth data from MongoDB, session:', sessionId);
        const response = await fetch(`${API_URL}/api/birth-details/load/${sessionId}`);
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            console.log('Loaded birth data from MongoDB:', result.data);
            localStorage.setItem('birthDetails', JSON.stringify(result.data));
            return result.data;
          }
        }
      }
      
      // Fallback to latest entry ONLY if no local data exists
      console.log('Trying to load latest birth data from MongoDB...');
      const latestResponse = await fetch(`${API_URL}/api/birth-details/load-latest`);
      if (latestResponse.ok) {
        const latestResult = await latestResponse.json();
        if (latestResult.success && latestResult.data) {
          console.log('Loaded latest birth data from MongoDB:', latestResult.data);
          localStorage.setItem('birthDetails', JSON.stringify(latestResult.data));
          return latestResult.data;
        }
      }
    } catch (e) {
      console.log('MongoDB load failed:', e.message);
    }
    return null;
  };

  // Parse birth data from localStorage or raw data
  const parseBirthData = (rawData = null) => {
    try {
      let birthData = rawData;
      if (!birthData) {
        let storedData = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
        if (!storedData) return null;
        birthData = JSON.parse(storedData);
      }
      
      const dateParts = (birthData.dateOfBirth || '').split(/[-\/]/);
      let day, month, year;
      if (dateParts.length === 3) {
        if (dateParts[0].length === 4) {
          year = parseInt(dateParts[0]);
          month = parseInt(dateParts[1]);
          day = parseInt(dateParts[2]);
        } else {
          day = parseInt(dateParts[0]);
          month = parseInt(dateParts[1]);
          year = parseInt(dateParts[2]);
        }
      }
      
      let hour = 12, minute = 0;
      const timeMatch = birthData.timeOfBirth?.match(/(\d+):(\d+)\s*(AM|PM)?/i);
      if (timeMatch) {
        hour = parseInt(timeMatch[1]);
        minute = parseInt(timeMatch[2]);
        const period = timeMatch[3]?.toUpperCase();
        if (period === 'PM' && hour !== 12) hour += 12;
        if (period === 'AM' && hour === 12) hour = 0;
      }
      
      return {
        name: birthData.name || 'User',
        year, month, day, hour, minute,
        latitude: birthData.latitude || 28.6139,
        longitude: birthData.longitude || 77.2090,
        gender: birthData.gender?.toLowerCase() || 'male',
        place: birthData.place || birthData.placeOfBirth || '',
        phone: birthData.phone || ''
      };
    } catch (e) {
      console.error('Error parsing birth data:', e);
      return null;
    }
  };

  // Get unique user ID for MongoDB storage
  const getUserId = () => {
    // PRIORITY 1: Use logged-in user_id first (from auth)
    const loggedInUserId = localStorage.getItem('user_id');
    if (loggedInUserId) return loggedInUserId;
    
    // PRIORITY 2: Use device_id for user isolation (set during onboarding)
    const deviceId = localStorage.getItem('taraanaya_device_id');
    if (deviceId) return deviceId;
    
    // PRIORITY 3: Create unique ID from birth data + timestamp
    try {
      const birthDetails = localStorage.getItem('birthDetails') || localStorage.getItem('birth_details');
      if (birthDetails) {
        const data = JSON.parse(birthDetails);
        // Use session_id if available, otherwise create unique ID
        if (data.session_id) return data.session_id;
        // Create unique ID with timestamp to ensure isolation
        const uniqueId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        return uniqueId;
      }
    } catch (e) { /* ignore parse errors */ }
    
    // FALLBACK: Generate completely unique ID
    return `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Save audio cache to MongoDB
  const saveAudioToMongoDB = async (cacheType, audioData) => {
    try {
      const userId = getUserId();
      console.log(`💾 Saving ${cacheType} audio to MongoDB for user: ${userId}`);
      
      const response = await fetch(`${API_URL}/api/audio-cache/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          cache_type: cacheType,
          audio_data: audioData
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log(`✅ ${cacheType} audio saved to MongoDB:`, result.message);
        // Store user_id in localStorage for retrieval
        localStorage.setItem('audio_user_id', userId);
        return true;
      } else {
        console.log(`❌ Failed to save ${cacheType} audio:`, response.status);
        return false;
      }
    } catch (e) {
      console.log(`❌ MongoDB save error for ${cacheType}:`, e.message);
      return false;
    }
  };

  // Generate ALL predictions + TTS during loading
  const generateAllPredictions = async () => {
    // STEP 0: Try to load from MongoDB first
    console.log('🔍 Attempting to load birth data from MongoDB...');
    console.log('🌐 Using API_URL:', API_URL);
    
    const mongoData = await loadBirthDataFromMongoDB();
    
    // Parse birth data (from MongoDB or localStorage)
    let birthData = parseBirthData(mongoData);
    
    if (!birthData || !birthData.year) {
      console.error('❌ No birth data found in MongoDB or localStorage');
      // Try one more time with a small delay (race condition handling)
      await new Promise(resolve => setTimeout(resolve, 500));
      birthData = parseBirthData();
      if (!birthData || !birthData.year) {
        console.error('❌ Still no birth data after retry, redirecting to form');
        setTimeout(() => navigate('/onboarding-new'), 1000);
        return;
      }
    }
    
    console.log('✅ Birth data loaded successfully:', birthData);

    try {
      setProgress(10);
      const userId = getUserId();
      localStorage.setItem('audio_user_id', userId);
      
      // ========== NAME-BASED CACHE INVALIDATION ==========
      // If the person's name changed, clear ALL cached story data to prevent data mixing
      const currentName = (birthData.name || '').toLowerCase().trim();
      const cachedStoryRaw = localStorage.getItem('unified_story_data');
      if (cachedStoryRaw) {
        try {
          const cachedStory = JSON.parse(cachedStoryRaw);
          const cachedName = (cachedStory.user_info?.name || '').toLowerCase().trim();
          if (currentName && cachedName && currentName !== cachedName) {
            console.log(`Name changed: "${cachedName}" -> "${currentName}". Clearing all story caches.`);
            localStorage.removeItem('unified_story_data');
            localStorage.removeItem('ai_story_data');
            localStorage.removeItem('domain_insights_cache');
            localStorage.removeItem('detailed_yearly_report');
            localStorage.removeItem('past_story_audio_cache');
            localStorage.removeItem('future_reels_audio_cache');
            // Clear timeline-specific caches to prevent stale data for new users
            localStorage.removeItem('health_timeline_data');
            localStorage.removeItem('timeline_data_cache');
            localStorage.removeItem('detailed_monthly_data');
            localStorage.removeItem('domain_report_cache');
            // Clear individual domain timeline caches
            ['health', 'career', 'wealth', 'marriage', 'education', 'spiritual', 'travel', 'family'].forEach(d => {
              localStorage.removeItem(`${d}_timeline_data`);
            });
          }
        } catch (e) { /* ignore parse errors */ }
      }
      
      // ========== STEP 1: GENERATE UNIFIED STORY (FAST - Uses Cache) ==========
      console.log('========================================');
      console.log('🌟 STEP 1: Generating Story Data (with caching)');
      console.log('========================================');
      
      setProgress(15);
      
      let unifiedStoryData = null;
      let aiContentGenerated = false;
      
      // ========== FAST UNIFIED STORY CALL (Has Caching) ==========
      try {
        console.log('Calling Unified Story API...');
        setProgress(20);
        
        const requestBody = JSON.stringify({ 
          ...birthData, 
          user_id: userId,
          device_id: localStorage.getItem('device_id') || localStorage.getItem('taraanaya_device_id') || '',
          generate_overview: true,
          include_sections: ['past_mahadashas', 'current_mahadasha', 'future_mahadashas']
        });

        // Helper: single POST to the generate endpoint (called ONCE to trigger generation)
        const callGenerate = async () => {
          const resp = await fetch(`${API_URL}/api/unified-story/generate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: requestBody
          });
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          return resp.json();
        };

        // First call - triggers generation or returns cached data
        let unifiedResult = await callGenerate();
        setProgress(30);

        // ===== CHECK IF STORY WAS ALREADY VIEWED ON ANOTHER DEVICE =====
        // If user saw Past Story on /watch (web) and now opened the app, skip to Login
        if (unifiedResult?.already_viewed && unifiedResult?.from_cache && !isLandingSource) {
          console.log('Story already viewed on another device (web /watch). Skipping to login.');
          // Store the story data so dashboard can use it
          if (unifiedResult?.data) {
            localStorage.setItem('unified_story_data', JSON.stringify(unifiedResult.data));
          }
          localStorage.setItem('past_story_viewed', 'true');
          localStorage.setItem('free_story_used', 'true');
          localStorage.setItem('onboarding_completed', 'true');
          localStorage.removeItem('watching_story');
          localStorage.removeItem('story_generation_in_progress');
          // Sync gate flags to MongoDB (survives uninstall)
          const uid = localStorage.getItem('user_id');
          if (uid) {
            fetch(`${API_URL}/api/auth/gate-flags`, {
              method: 'POST', headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ user_id: uid, past_story_viewed: true, free_story_used: true })
            }).catch(() => {});
          }
          navigate('/login', { replace: true });
          return true;
        }

        // ===== POLLING LOOP: use lightweight GET status check instead of re-POSTing =====
        const MAX_POLL_TIME = 600000; // 10 min max (handles concurrent users gracefully)
        const POLL_INTERVAL = 5000;   // 5 sec between polls
        const pollStart = Date.now();
        let pollCount = 0;
        const cacheKey = unifiedResult?.data?.cache_key;

        while (
          unifiedResult?.success && 
          unifiedResult?.data?.status === 'generating' && 
          (Date.now() - pollStart) < MAX_POLL_TIME
        ) {
          pollCount++;
          const elapsed = Math.round((Date.now() - pollStart) / 1000);
          console.log(`Generation in progress... polling #${pollCount} (${elapsed}s elapsed)`);
          
          // Smooth asymptotic progress: climbs from 20→75 faster
          // Uses 1 - e^(-t/25) curve — reaches ~50% in 25s, ~70% in 50s
          const t = (Date.now() - pollStart) / 1000;
          const smoothProgress = Math.round(20 + 55 * (1 - Math.exp(-t / 25)));
          setProgress(smoothProgress);
          
          // Wait before next poll
          await new Promise(r => setTimeout(r, POLL_INTERVAL));
          
          // Use GET status endpoint instead of re-POSTing to generate
          if (cacheKey) {
            try {
              const statusResp = await fetch(`${API_URL}/api/unified-story/status/${cacheKey}?device_id=${encodeURIComponent(localStorage.getItem("taraanaya_device_id") || localStorage.getItem("device_id") || "")}`);
              if (statusResp.ok) {
                unifiedResult = await statusResp.json();
                // Break early if past_ready — we have enough to show the story
                if (unifiedResult?.data?.status === 'past_ready' && unifiedResult?.data?.past_story) {
                  console.log('🎯 Past story ready early!');
                  setProgress(76);
                  break;
                }
              } else {
                // Fallback to POST if status endpoint fails
                unifiedResult = await callGenerate();
              }
            } catch (e) {
              console.log('Status poll error, falling back to POST:', e.message);
              unifiedResult = await callGenerate();
            }
          } else {
            // No cache key available, use POST
            unifiedResult = await callGenerate();
          }
        }

        setProgress(76);
        if (unifiedResult?.data?.status === 'failed') {
          console.log('Generation failed on server, auto-retrying:', unifiedResult?.data?.error);
          setProgress(10);
          // Clear the failed cache and retry
          unifiedResult = await callGenerate();
          const retryPollStart = Date.now();
          while (
            unifiedResult?.success && 
            unifiedResult?.data?.status === 'generating' && 
            (Date.now() - retryPollStart) < MAX_POLL_TIME
          ) {
            const retryT = (Date.now() - retryPollStart) / 1000;
          const retrySmooth = Math.round(15 + 55 * (1 - Math.exp(-retryT / 40)));
          setProgress(retrySmooth);
            await new Promise(r => setTimeout(r, POLL_INTERVAL));
            if (cacheKey) {
              try {
                const sr = await fetch(`${API_URL}/api/unified-story/status/${cacheKey}?device_id=${encodeURIComponent(localStorage.getItem("taraanaya_device_id") || localStorage.getItem("device_id") || "")}`);
                if (sr.ok) unifiedResult = await sr.json();
              } catch (e) { unifiedResult = await callGenerate(); }
            }
          }
          if (unifiedResult?.data?.status === 'failed' || unifiedResult?.data?.status === 'generating') {
            setProgress(0);
            setDataGenerated(false);
            setGenerationFailed(true);
            return false;
          }
        }
        
        // Handle partial data (past_ready) — show past story immediately
        if (unifiedResult?.data?.status === 'past_ready' && unifiedResult?.data?.past_story) {
          console.log('🎯 Past story ready! Showing immediately while rest generates...');
          setProgress(75);
          unifiedStoryData = unifiedResult.data;
          // Store cache_key for on-demand premium content generation
          if (cacheKey) localStorage.setItem('story_cache_key', cacheKey);
          // Remove status field so downstream code treats it as complete data
          delete unifiedStoryData.status;
          delete unifiedStoryData.partial;
          
          // Background poll for complete data (with future reels)
          if (cacheKey) {
            const bgPoll = async () => {
              const BG_MAX = 180000; // 3 min max
              const bgStart = Date.now();
              while ((Date.now() - bgStart) < BG_MAX) {
                await new Promise(r => setTimeout(r, 8000));
                try {
                  const sr = await fetch(`${API_URL}/api/unified-story/status/${cacheKey}?device_id=${encodeURIComponent(localStorage.getItem("taraanaya_device_id") || localStorage.getItem("device_id") || "")}`);
                  if (!sr.ok) continue;
                  const fullResult = await sr.json();
                  const resData = fullResult?.data;
                  // Check if reels exist (completed status or cached data with reels)
                  const reels = resData?.future_story?.domain_reels;
                  if (reels) {
                    const hasReels = Object.values(reels).some(lr => 
                      lr && Object.values(lr).some(v => typeof v === 'string' && v.length > 50)
                    );
                    if (hasReels) {
                      // Update localStorage with complete data including reels
                      try {
                        const existing = JSON.parse(localStorage.getItem('unified_story_data') || '{}');
                        if (resData.future_story) existing.future_story = resData.future_story;
                        localStorage.setItem('unified_story_data', JSON.stringify(existing));
                        localStorage.removeItem('future_reels_generating');
                        window.dispatchEvent(new Event('future-reels-ready'));
                        console.log('✅ Background poll: Future reels loaded into localStorage');
                      } catch(e) { console.warn('BG poll localStorage error:', e); }
                      return;
                    }
                  }
                } catch(e) { /* ignore */ }
              }
            };
            bgPoll(); // Fire and forget
          }
        }
        
        if (unifiedResult?.success && unifiedResult?.data && !unifiedResult.data.status) {
          // Got actual story data (either from cache or completed generation)
          unifiedStoryData = unifiedResult.data;
          
          console.log('Unified Story Generated:', {
            sections: unifiedStoryData.sections?.length || 0,
            from_cache: unifiedResult.from_cache || false,
            all_mahadashas: unifiedStoryData.all_mahadashas?.length || 0,
            polls: pollCount
          });
          
          // Build AI content data from unified story
          const aiContentData = {
            ai_generated: true,
            generated_at: new Date().toISOString(),
            user_info: { name: birthData.name || 'User' },
            past_story: unifiedStoryData.past_story || {
              intro: {
                hinglish: `Namaste ${birthData.name || 'User'}! Yeh hai tumhari life ki kahani.`,
                hindi: `नमस्ते ${birthData.name || 'User'}! यह है आपके जीवन की कहानी।`,
                english: `Hello ${birthData.name || 'User'}! This is the story of your life.`
              },
              sections: {},
              timeline_narrations: { hinglish: [], hindi: [], english: [] },
              mahadashas: []
            },
            all_mahadashas: unifiedStoryData.all_mahadashas || [],
            past_mahadashas: unifiedStoryData.past_mahadashas || unifiedStoryData.past_story?.mahadashas || [],
            current_mahadasha: unifiedStoryData.current_mahadasha || {},
            future_mahadashas: unifiedStoryData.future_mahadashas || [],
            sections: unifiedStoryData.sections || [],
            kernel: unifiedStoryData.kernel || {},
            meta: { generated_at: new Date().toISOString() }
          };
          
          // Cache to localStorage
          try {
            localStorage.setItem('ai_story_data', JSON.stringify(aiContentData));
            localStorage.setItem('unified_story_data', JSON.stringify(unifiedStoryData));
            // Store cache_key for on-demand premium content generation
            if (cacheKey) {
              localStorage.setItem('story_cache_key', cacheKey);
            }
            if (unifiedStoryData.domain_insights) {
              localStorage.setItem('domain_insights_cache', JSON.stringify(unifiedStoryData.domain_insights));
            }
            if (unifiedStoryData.yearly_predictions) {
              localStorage.setItem('detailed_yearly_report', JSON.stringify({
                year: new Date().getFullYear(),
                data: unifiedStoryData.yearly_predictions
              }));
            }
            console.log('Story data cached to localStorage');
          } catch (e) {
            console.log('localStorage quota exceeded');
          }
          
          aiContentGenerated = true;
          setProgress(78);
          
          // Save to MongoDB
          try {
            const deviceId = localStorage.getItem('taraanaya_device_id');
            await fetch(`${API_URL}/api/unified-story/save-story`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                user_id: userId, 
                story_data: unifiedStoryData,
                device_id: deviceId 
              })
            });
            console.log('Story saved to MongoDB');
          } catch (saveError) {
            console.log('MongoDB save error:', saveError.message);
          }
        } else if (unifiedResult?.data?.status === 'generating') {
          console.log('Generation still in progress after polling - auto-retrying...');
          // Don't give up - restart polling with a new POST
          setProgress(15);
          unifiedResult = await callGenerate();
          // Continue polling again
          const pollStart2 = Date.now();
          while (
            unifiedResult?.success && 
            unifiedResult?.data?.status === 'generating' && 
            (Date.now() - pollStart2) < MAX_POLL_TIME
          ) {
            const poll2T = (Date.now() - pollStart2) / 1000;
          const poll2Smooth = Math.round(20 + 55 * (1 - Math.exp(-poll2T / 40)));
          setProgress(poll2Smooth);
            await new Promise(r => setTimeout(r, POLL_INTERVAL));
            if (cacheKey) {
              try {
                const sr = await fetch(`${API_URL}/api/unified-story/status/${cacheKey}?device_id=${encodeURIComponent(localStorage.getItem("taraanaya_device_id") || localStorage.getItem("device_id") || "")}`);
                if (sr.ok) unifiedResult = await sr.json();
              } catch (e) { unifiedResult = await callGenerate(); }
            } else {
              unifiedResult = await callGenerate();
            }
          }
          // If still generating after second round, NOW show retry
          if (unifiedResult?.data?.status === 'generating') {
            setProgress(0);
            setDataGenerated(false);
            setGenerationFailed(true);
            return false;
          }
        }
      } catch (unifiedError) {
        console.log('Unified Story API error:', unifiedError.message);
        // API call failed completely - show error
        setProgress(0);
        setDataGenerated(false);
        setGenerationFailed(true);
        return false;
      }
      
      setProgress(79);
      
      // ========== STEP 2: SKIP TTS PRE-GENERATION (ON-DEMAND) ==========
      console.log('========================================');
      console.log('⚡ STEP 2: Skipping TTS pre-generation (on-demand optimization)');
      console.log('========================================');
      
      if (unifiedStoryData && unifiedStoryData.past_story) {
        setProgress(75);
        
        const pastMahadashas = unifiedStoryData.past_story.mahadashas || [];
        console.log('📝 Past Mahadashas count:', pastMahadashas.length);
        console.log('⚡ TTS will be generated on-demand (optimized loading)');
        
        // Just save empty audio cache with sections for text display
        const audioCache = { intro: {}, hinglish: {}, english: {}, hindi: {} };
        
        // Save metadata only to MongoDB (no audio)
        try {
          await saveAudioToMongoDB('past', audioCache);
        } catch (e) {
          console.log('Metadata save skipped:', e.message);
        }
        
        setProgress(80);
      } else {
        console.log('⚠️ No unified story data for past');
        setProgress(80);
      }
      
      // ========== STEP 3: SAVE FUTURE STORY DATA ==========
      console.log('========================================');
      console.log('⚡ STEP 3: Saving Future Story Data');
      console.log('========================================');
      setProgress(85);
      
      if (unifiedStoryData && unifiedStoryData.future_story) {
        const futureMahadashas = unifiedStoryData.future_story.mahadashas || [];
        const futureIntros = unifiedStoryData.future_story.intro || {};
        const futureTimelineNarrations = unifiedStoryData.future_story.timeline_narrations || {};
        
        console.log('📝 Future Mahadashas count:', futureMahadashas.length);
        console.log('📝 Future narrations langs:', Object.keys(futureTimelineNarrations));
        
        // Save future data to localStorage (wrapped in try-catch for quota issues)
        try {
          localStorage.setItem('unified_future_story', JSON.stringify({
            mahadashas: futureMahadashas,
            intro: futureIntros,
            timeline_narrations: futureTimelineNarrations,
            domains: unifiedStoryData.domains
          }));
        } catch (e) { console.log('⚠️ Skipping unified_future_story localStorage save'); }
        
        // Also save in old format for backward compatibility
        try {
          localStorage.setItem('health_future_story_data', JSON.stringify({
            name: birthData.name,
            timeline: futureMahadashas.map(md => ({
              dasha_lord: md.lord,
              years: md.years,
              is_current: md.status === 'current',
              health_type: md.domain_predictions?.health?.summary || 'Health period',
              risk_level: md.domain_predictions?.health?.risk_level || 'moderate'
            })),
            narration: {
              hinglish: futureIntros.hinglish + (futureTimelineNarrations.hinglish || []).map(n => n.text).join('\n'),
            english: futureIntros.english + (futureTimelineNarrations.english || []).map(n => n.text).join('\n'),
            hindi: futureIntros.hindi + (futureTimelineNarrations.hindi || []).map(n => n.text).join('\n')
          }
        }));
        } catch (e) { console.log('⚠️ Skipping health_future_story_data localStorage save'); }
        
        console.log('✅ Future story data processed');
        
        // OPTIMIZED: Skip TTS pre-generation for future story too
        // TTS will be generated on-demand when user plays
        console.log('⚡ Skipping Future TTS pre-generation (on-demand optimization)');
        
        setProgress(87);
        
        // ========== STEP 3.5: SAVE DOMAIN REEL TEXT ONLY (TTS on-demand) ==========
        console.log('========================================');
        console.log('🎯 STEP 3.5: Saving 8 Domain Reel Texts (TTS on-demand)');
        console.log('========================================');
        
        const domainReelsData = unifiedStoryData.future_story?.domain_reels || {};
        const DOMAIN_KEYS = ['health', 'career', 'education', 'wealth', 'spiritual', 'travel', 'family', 'marriage'];
        
        // Check if domain reels have multi-language structure
        const isMultiLang = domainReelsData.hinglish || domainReelsData.english || domainReelsData.hindi;
        
        // Fire ALL domain reel saves in parallel (non-blocking)
        const domainSavePromises = [];
        for (let domainIdx = 0; domainIdx < DOMAIN_KEYS.length; domainIdx++) {
          const domainKey = DOMAIN_KEYS[domainIdx];
          
          // Get domain reel text (handle both old and new structure)
          let domainReel = '';
          if (isMultiLang) {
            const hinglishData = domainReelsData.hinglish || {};
            const englishData = domainReelsData.english || {};
            const hinglishReels = hinglishData.reels || hinglishData;
            const englishReels = englishData.reels || englishData;
            domainReel = hinglishReels[domainKey] || englishReels[domainKey] || '';
          } else {
            domainReel = domainReelsData[domainKey] || '';
          }
          
          if (!domainReel || domainReel.length < 200) {
            continue;
          }
          
          // Clean the reel text
          const cleanedReel = domainReel.replace(/[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|❤️|💼|📚|💰|💍|🌍|✨|🌟|🙏/gu, '').trim();
          
          // Split into sections
          const sections = [];
          let currentSection = '';
          const paragraphs = cleanedReel.split(/\n\n+/);
          
          for (const para of paragraphs) {
            if ((currentSection + para).length > 1800) {
              if (currentSection.length > 100) sections.push(currentSection.trim());
              currentSection = para;
            } else {
              currentSection += (currentSection ? '\n\n' : '') + para;
            }
          }
          if (currentSection.length > 100) sections.push(currentSection.trim());
          if (sections.length === 0 && cleanedReel.length > 100) sections.push(cleanedReel);
          
          // Save in parallel (fire and forget with catch)
          domainSavePromises.push(
            fetch(`${API_URL}/api/audio-cache/save/${getUserId()}/future/${domainKey}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ data: {}, sections })
            }).catch(e => console.log(`${domainKey} save error`))
          );
        }
        
        // Wait for all domain saves in parallel (much faster than sequential)
        if (domainSavePromises.length > 0) {
          await Promise.allSettled(domainSavePromises);
        }
        
        console.log('✅ All domain reel texts saved in parallel');
      }
      
      setProgress(90);
      
      // Execute ONLY essential data for fast onboarding
      // Premium content (Insights, Timeline, Monthly, Yearly, AI Content) 
      // will be generated AFTER payment via post-payment loader
      console.log('========================================');
      console.log('STEP 4: Quick domain data load (parallel, essential only)');
      console.log('========================================');
      
      setProgress(92);
      
      const currentUserId = getUserId();
      
      // Only pre-generate the detailed report (needed for basic domain view)
      const birthPayload = {
        user_id: currentUserId,
        name: birthData.name || 'User',
        year: birthData.year,
        month: birthData.month,
        day: birthData.day,
        hour: birthData.hour || 12,
        minute: birthData.minute || 0,
        place: birthData.place || 'India',
        gender: birthData.gender || 'unknown',
        latitude: birthData.latitude || 28.6139,
        longitude: birthData.longitude || 77.209
      };

      const preGenerateReport = async () => {
        try {
          const res = await fetch(`${API_URL}/api/report/generate-detailed`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(birthPayload)
          });
          if (res.ok) {
            const data = await res.json();
            if (data.success && data.report) {
              try {
                localStorage.setItem(`detailed_report_${currentUserId}`, JSON.stringify(data.report));
                const birthHash = `${birthData.year}-${birthData.month}-${birthData.day}-${birthData.hour}-${birthData.minute}-${birthData.latitude}-${birthData.longitude}`;
                localStorage.setItem(`birth_hash_${currentUserId}`, birthHash);
                console.log('Detailed report pre-cached');
              } catch(e) { /* quota */ }
            }
          }
        } catch (e) {
          console.log('Report pre-generation error:', e.message);
        }
      };

      // Only run the essential report generation
      await preGenerateReport();
      
      // CRITICAL GUARD: Only proceed to story if generation succeeded
      if (!aiContentGenerated) {
        console.log('Story generation did not complete - staying on loading page');
        setProgress(0);
        setDataGenerated(false);
        setGenerationFailed(true);
        return false;
      }
      
      setProgress(95);
      
      // Skip ALL premium pre-generation:
      // - Enhanced Insights → loaded after payment or on-demand
      // - AI Content (GPT) → loaded after payment or on-demand
      // - Timeline/Monthly/Yearly → loaded after payment (₹999 plan)
      // - Domain timelines → loaded on-demand when user visits
      // - Future Reels TTS → loaded on-demand when user plays
      console.log('Skipping premium content pre-generation (will load after payment)');
      console.log('Premium content: Insights, Timeline, Monthly, Yearly, AI Content, Domain Timelines');
          
          setProgress(98);
          
          // Log final status
          console.log('========================================');
          console.log('FINAL STATUS: All data loaded to MongoDB');
          console.log('========================================');
          
          // Story text ready — start audio generation (same screen, planets light up)
          setDataGenerated(true);
          localStorage.setItem('story_generated', 'true');
          // Don't set watching_story here — only set when user clicks planet or "View All"
          // Setting it here causes App.js to auto-redirect to /your-story
          localStorage.setItem('onboarding_completed', 'true');
          localStorage.removeItem('story_generation_in_progress');
          
          // Audio generation now handled by LoadingPastStoryReels (embedded)
          // It detects unified_story_data in localStorage and starts generating
          
          return true;
      
    } catch (err) {
      console.error('Error generating predictions:', err);
      setGenerationFailed(true);
      setProgress(0);
      return false;
    }
  };

  const handleRetry = async () => {
    setGenerationFailed(false);
    setProgress(5);
    generationStartedRef.current = false;
    await generateAllPredictions();
  };

  useEffect(() => {
    let isMounted = true;
    trackPageView('story_generation');
    trackEvent('story_generation_start', { source: isLandingSource ? 'landing' : 'app' });
    
    const runGeneration = async () => {
      // GUARD: Prevent duplicate execution
      if (!isMounted || generationStartedRef.current) return;
      generationStartedRef.current = true;
      // Clear old planet state for fresh generation
      localStorage.removeItem('past_reels_gen_state');
      await generateAllPredictions();
    };
    
    runGeneration();
    
    // Rotate fun facts
    const factInterval = setInterval(() => {
      setCurrentFactIndex((prev) => (prev + 1) % funFacts.length);
    }, 4000);
    
    return () => {
      isMounted = false;
      clearInterval(factInterval);
    };
  }, []);


  // Show LoadingPastStoryReels directly — planets visible from start
  // Story gen happens in background (useEffect above), planets are dim until audio phase
  return <LoadingPastStoryReels embedded storyProgress={progress} generationFailed={generationFailed} />;
};

export default LoadingStoryGenerator;
